import { useState, useRef, useMemo } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { CalendarBlankIcon } from "@phosphor-icons/react/CalendarBlank";
import { XIcon } from "@phosphor-icons/react/X";
import { Icon } from "../../Icons/Icon";
import { PortalContextReset } from "../../Controls/Internal/PortalContextReset";
import { useMarkDialogDirty } from "../../Dialog/FormDirtyContext";
import { useControlPresentation } from "../../Controls/Internal/useControlPresentation";
import {
  dateTriggerText,
  dateTriggerUnderline,
} from "../Internal/dateFieldStyles";
import {
  fromJsDate,
  toJsDate,
  formatLocalDate,
} from "@freightsimple/time/helpers";
import type { LocalDate } from "@freightsimple/time/branded-types";
import { Calendar } from "../Internal/Calendar";
import {
  generatePresets,
  type DateRangePresetGroup,
} from "../Internal/presets";
import type { DateRange } from "react-day-picker";
import { usePopoverZIndex } from "../../Theme/overlayZIndex";

interface DateRangePickerBaseProps {
  value?: [LocalDate | undefined, LocalDate | undefined] | undefined;
  placeholder?: [string, string];
  disabled?: boolean;
  allowClear?: boolean;
  disabledDate?: (date: LocalDate) => boolean;
}

/**
 * `allowOpenEnded` widens `onChange` rather than being a plain boolean, so a
 * caller that hasn't opted in never has to handle an undefined end. Opting in is
 * what makes the wider type appear.
 *
 * It exists because picking a start and stopping usually means one day, which is
 * what every existing caller expects — but a *filter* is the exception. "Booked
 * from 1 Jan onwards" is a normal thing to ask, and collapsing it to "booked on
 * 1 Jan" silently returns the wrong shipments.
 */
type DateRangePickerProps = DateRangePickerBaseProps &
  (
    | {
        allowOpenEnded?: false | undefined;
        onChange?: (range: [LocalDate, LocalDate] | undefined) => void;
      }
    | {
        allowOpenEnded: true;
        onChange?: (
          range: [LocalDate, LocalDate | undefined] | undefined,
        ) => void;
      }
  );

const TRIGGER_DATE_FORMAT = "ddd MMM D, YYYY";

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function DateRangePicker(props: DateRangePickerProps) {
  const popoverZ = usePopoverZIndex();
  const {
    value,
    placeholder = ["Start date", "End date"],
    disabled = false,
    allowClear = false,
    disabledDate,
  } = props;
  const allowOpenEnded = props.allowOpenEnded === true;

  /**
   * One place where the two callback shapes meet. When open-ended isn't allowed
   * the end is never undefined — handleSelect fills it with the start — so the
   * narrow caller only ever sees a complete pair.
   */
  function emit(range: [LocalDate, LocalDate | undefined] | undefined): void {
    if (props.allowOpenEnded === true) {
      props.onChange?.(range);
      return;
    }
    if (range === undefined) {
      props.onChange?.(undefined);
      return;
    }
    if (range[1] !== undefined) {
      props.onChange?.([range[0], range[1]]);
    }
  }
  const markDirty = useMarkDialogDirty();
  const underlined = useControlPresentation() === "underlined";
  const [open, setOpen] = useState(false);
  // oxlint-disable-next-line fs/no-null -- DOM refs require null
  const anchorRef = useRef<HTMLDivElement>(null);

  const startValue = value?.[0] ?? undefined;
  const endValue = value?.[1] ?? undefined;

  const selected: DateRange | undefined =
    startValue || endValue
      ? {
          from: startValue ? toJsDate(startValue) : undefined,
          to: endValue ? toJsDate(endValue) : undefined,
        }
      : undefined;

  const presetGroups = useMemo(() => generatePresets(new Date()), []);

  function handleSelect(range: DateRange | undefined): void {
    markDirty?.();
    if (!range?.from) {
      emit(undefined);
      return;
    }
    const start = fromJsDate(range.from);
    const end = range.to
      ? fromJsDate(range.to)
      : allowOpenEnded
        ? undefined
        : start;
    emit([start, end]);
  }

  function handlePreset(start: LocalDate, end: LocalDate): void {
    markDirty?.();
    emit([start, end]);
    setOpen(false);
  }

  function handleClear(e: React.MouseEvent): void {
    e.stopPropagation();
    markDirty?.();
    emit(undefined);
  }

  // With the year. A range can span a year boundary — a twelve-month window always does — and
  // without it "Sun August 17th to Mon August 17th" reads as one day rather than a full year. The
  // month is abbreviated to buy back the width the year costs.
  const startText = startValue
    ? formatLocalDate(startValue, TRIGGER_DATE_FORMAT)
    : undefined;
  const endText = endValue
    ? formatLocalDate(endValue, TRIGGER_DATE_FORMAT)
    : undefined;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Anchor asChild>
        <div
          ref={anchorRef}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onClick={() => !disabled && !open && setOpen(true)}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !disabled && !open) {
              e.preventDefault();
              setOpen(true);
            }
          }}
          className={
            underlined
              ? dateTriggerUnderline({ open, disabled })
              : `group inline-flex h-8 cursor-pointer items-center gap-2 rounded-md border border-border-strong bg-card px-2.5 transition-colors ${disabled ? "pointer-events-none opacity-40" : "hover:border-brand"}`
          }
        >
          <Icon
            icon={CalendarBlankIcon}
            size="sm"
            color="text-muted-foreground"
          />
          <span
            className={
              underlined
                ? dateTriggerText(!!startText)
                : `text-xs font-medium ${startText ? "text-foreground" : "text-faint"}`
            }
          >
            {startText ?? placeholder[0]}
          </span>
          <span
            className={underlined ? "text-xl text-faint" : "text-xs text-faint"}
          >
            →
          </span>
          <span
            className={
              underlined
                ? dateTriggerText(!!endText)
                : `text-xs font-medium ${endText ? "text-foreground" : "text-faint"}`
            }
          >
            {endText ?? placeholder[1]}
          </span>
          {allowClear && (startValue || endValue) && !disabled && (
            <button
              type="button"
              aria-label="Clear date range"
              tabIndex={-1}
              onClick={handleClear}
              onMouseDown={(e) => e.preventDefault()}
              className="shrink-0 text-faint opacity-0 transition-opacity group-hover:opacity-100 hover:text-muted-foreground"
            >
              <Icon icon={XIcon} size="xs" />
            </button>
          )}
        </div>
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          style={{ zIndex: popoverZ }}
          align="start"
          sideOffset={4}
          className="flex rounded-md border border-border bg-card shadow-floating"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (
              e.target instanceof Node &&
              anchorRef.current?.contains(e.target)
            ) {
              e.preventDefault();
            }
          }}
        >
          {/* Portaled content must not inherit the trigger surface's layout
              contexts — see PortalContextReset. */}
          <PortalContextReset>
            <PresetSidebar_ groups={presetGroups} onSelect={handlePreset} />
            <div className="border-l border-border">
              <Calendar
                mode="range"
                selected={selected}
                onSelect={handleSelect}
                numberOfMonths={2}
                disabled={
                  disabledDate
                    ? (date) => disabledDate(fromJsDate(date))
                    : undefined
                }
              />
            </div>
          </PortalContextReset>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

function PresetSidebar_({
  groups,
  onSelect,
}: {
  groups: DateRangePresetGroup[];
  onSelect: (start: LocalDate, end: LocalDate) => void;
}) {
  const [activeGroup, setActiveGroup] = useState(0);
  const group = groups[activeGroup];

  return (
    <div className="flex max-h-96 w-60 flex-col">
      <div className="flex shrink-0 border-b border-border">
        {groups.map((g, i) => (
          <button
            key={g.label}
            type="button"
            onClick={() => setActiveGroup(i)}
            className={`flex-1 px-2 py-2.5 text-xs font-bold transition-colors ${
              i === activeGroup
                ? "-mb-px border-b-2 border-brand text-brand-text"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {g.label}
          </button>
        ))}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
        {group?.presets.map((preset) => (
          <button
            key={preset.label}
            type="button"
            onClick={() => onSelect(preset.start, preset.end)}
            className="w-full rounded-md px-3 py-2 text-left text-sm font-medium text-foreground transition-colors hover:bg-brand hover:text-white"
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  );
}
